import React, { useState, useMemo } from "react";
import {
  Users, UserPlus, X, RefreshCw, ChevronRight, ArrowLeft,
  FileText, CheckCircle, AlertTriangle, Clock, Calendar,
  MapPin, Shield, BarChart3, Mic, Mail, Phone,
} from "lucide-react";

function StatusDot({ status }) {
  const colors = { "in-session": "#68d391", available: "#63b3ed", documenting: "#ecc94b", traveling: "#f6ad55", offline: "#4a5568" };
  return (
    <span style={{
      width: 8, height: 8, borderRadius: "50%", display: "inline-block",
      background: colors[status] || "#4a5568",
      boxShadow: status === "in-session" ? "0 0 8px #68d391" : "none",
    }} />
  );
}

function MemberProfile({ member, notes, individuals, C, onBack }) {
  const [activeTab, setActiveTab] = useState("overview");

  const memberNotes = useMemo(() =>
    notes.filter(n => n.agentId === member.id)
      .sort((a, b) => {
        const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || a.date || 0);
        const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || b.date || 0);
        return db - da;
      }),
    [notes, member.id]
  );

  const caseload = useMemo(() =>
    individuals.filter(ind =>
      (ind.assignedAgents || []).includes(member.id) ||
      ind.primaryAgent === member.id
    ),
    [individuals, member.id]
  );

  const signed = memberNotes.filter(n => n.supervisorReviewed).length;
  const unsigned = memberNotes.filter(n => !n.supervisorReviewed).length;
  const flagged = memberNotes.filter(n => n.hasPriorityFlags).length;
  const signRate = memberNotes.length > 0 ? Math.round((signed / memberNotes.length) * 100) : 0;

  // Notes this week
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekNotes = memberNotes.filter(n => {
    const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date || 0);
    return d >= weekAgo;
  });

  // Notes this month
  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  const monthNotes = memberNotes.filter(n => {
    const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date || 0);
    return d >= monthAgo;
  });

  // Service type breakdown
  const serviceBreakdown = useMemo(() => {
    const map = {};
    memberNotes.forEach(n => {
      const svc = n.serviceType || n.noteData?.header?.serviceType || "ICM";
      if (!map[svc]) map[svc] = 0;
      map[svc]++;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [memberNotes]);

  // Daily activity (last 14 days)
  const dailyActivity = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toLocaleDateString();
      const count = memberNotes.filter(n => {
        const nd = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date || 0);
        return nd.toLocaleDateString() === dateStr;
      }).length;
      days.push({ label: d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" }), count });
    }
    return days;
  }, [memberNotes]);

  const maxDaily = Math.max(...dailyActivity.map(d => d.count), 1);

  const roleColors = {
    agent: { bg: "linear-gradient(135deg, #63b3ed, #4fd1c5)", badge: "rgba(99,179,237,0.08)", text: "#63b3ed" },
    counselor: { bg: "linear-gradient(135deg, #b794f4, #63b3ed)", badge: "rgba(183,148,244,0.1)", text: "#b794f4" },
    supervisor: { bg: "linear-gradient(135deg, #ecc94b, #f6ad55)", badge: "rgba(236,201,75,0.1)", text: "#ecc94b" },
    admin: { bg: "linear-gradient(135deg, #ecc94b, #f6ad55)", badge: "rgba(236,201,75,0.1)", text: "#ecc94b" },
  };
  const rc = roleColors[member.role] || roleColors.agent;

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "notes", label: `Notes (${memberNotes.length})` },
    { key: "caseload", label: `Caseload (${caseload.length})` },
  ];

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 16,
        background: "none", border: "none", cursor: "pointer", color: "#63b3ed", fontSize: 12, fontWeight: 700,
      }}>
        <ArrowLeft size={14} /> Back to Team
      </button>

      {/* Profile Header */}
      <div style={{ ...C.card, padding: 20 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: rc.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 900, color: "white", flexShrink: 0,
          }}>
            {(member.displayName || member.email || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 900 }}>{member.displayName || member.email}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase",
                background: rc.badge, color: rc.text,
              }}>{member.role}</span>
              <StatusDot status={member.liveStatus || "offline"} />
              <span style={{ fontSize: 10, color: "#4a5568", textTransform: "capitalize" }}>
                {(member.liveStatus || "offline").replace("-", " ")}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#718096", marginTop: 6 }}>
              {member.credentials && <span>{member.credentials} · </span>}
              {member.program && <span>{member.program} · </span>}
              {member.email}
            </div>
            {member.phone && (
              <div style={{ fontSize: 11, color: "#718096", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                <Phone size={10} /> {member.phone}
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 16 }}>
          {[
            { label: "This Week", value: weekNotes.length, color: "#63b3ed" },
            { label: "This Month", value: monthNotes.length, color: "#4fd1c5" },
            { label: "All Time", value: memberNotes.length, color: "#b794f4" },
            { label: "Caseload", value: caseload.length, color: "#ecc94b" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center", padding: "8px 0", borderRadius: 10, background: "rgba(15,23,42,0.5)" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, margin: "16px 0 12px" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
            background: activeTab === t.key ? "rgba(49,130,206,0.15)" : "transparent",
            color: activeTab === t.key ? "#63b3ed" : "#718096",
            border: `1px solid ${activeTab === t.key ? "rgba(49,130,206,0.2)" : "transparent"}`,
          }}>{t.label}</button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && (
        <div>
          {/* Activity Chart */}
          <div style={{ ...C.card, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>14-Day Activity</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 60 }}>
              {dailyActivity.map((d, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ fontSize: 7, color: "#63b3ed", fontWeight: 700, marginBottom: 2 }}>
                    {d.count > 0 ? d.count : ""}
                  </div>
                  <div style={{
                    width: "100%", borderRadius: "3px 3px 0 0",
                    height: Math.max(2, (d.count / maxDaily) * 44),
                    background: d.count > 0 ? "linear-gradient(to top, rgba(99,179,237,0.3), rgba(99,179,237,0.6))" : "rgba(99,179,237,0.06)",
                  }} />
                  <div style={{ fontSize: 6, color: "#4a5568", marginTop: 2 }}>{d.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Co-sign Rate */}
          <div style={{ ...C.card, padding: 16, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800 }}>Co-Sign Rate</span>
              <span style={{ fontSize: 14, fontWeight: 900, fontFamily: "monospace", color: signRate === 100 ? "#68d391" : signRate > 50 ? "#ecc94b" : "#fc8181" }}>
                {signRate}%
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "rgba(99,179,237,0.06)" }}>
              <div style={{
                height: "100%", borderRadius: 3, width: `${signRate}%`,
                background: signRate === 100 ? "#68d391" : signRate > 50 ? "#ecc94b" : "#fc8181",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: "#68d391" }}>{signed} signed</span>
              {unsigned > 0 && <span style={{ fontSize: 10, color: "#fc8181" }}>{unsigned} unsigned</span>}
              {flagged > 0 && <span style={{ fontSize: 10, color: "#fc8181" }}>{flagged} flagged</span>}
            </div>
          </div>

          {/* Service Breakdown */}
          <div style={{ ...C.card, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Service Type Breakdown</div>
            {serviceBreakdown.length === 0 ? (
              <div style={{ fontSize: 12, color: "#4a5568", textAlign: "center", padding: 16 }}>No notes yet</div>
            ) : (
              serviceBreakdown.map(([svc, count], i) => {
                const pct = memberNotes.length > 0 ? Math.round((count / memberNotes.length) * 100) : 0;
                return (
                  <div key={svc} style={{ marginBottom: i < serviceBreakdown.length - 1 ? 8 : 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{svc}</span>
                      <span style={{ fontSize: 10, color: "#718096" }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ height: 3, borderRadius: 2, background: "rgba(99,179,237,0.06)" }}>
                      <div style={{ height: "100%", borderRadius: 2, width: `${pct}%`, background: "linear-gradient(90deg, #3182ce, #4fd1c5)" }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Last Active */}
          <div style={{ fontSize: 10, color: "#4a5568", textAlign: "center", marginTop: 12 }}>
            Last login: {member.lastLogin?.toDate ? member.lastLogin.toDate().toLocaleString() : "Unknown"}
            {member.createdAt?.toDate && <span> · Joined: {member.createdAt.toDate().toLocaleDateString()}</span>}
          </div>
        </div>
      )}

      {/* NOTES TAB */}
      {activeTab === "notes" && (
        <div>
          {memberNotes.length === 0 ? (
            <div style={{ ...C.card, textAlign: "center", padding: "32px 0" }}>
              <FileText size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
              <div style={{ fontSize: 13, color: "#4a5568" }}>No notes on record</div>
            </div>
          ) : (
            memberNotes.slice(0, 20).map((note, i) => {
              const date = note.createdAt?.toDate ? note.createdAt.toDate().toLocaleDateString() : note.date || "—";
              return (
                <div key={note.id || i} style={{
                  ...C.card, marginBottom: 8, padding: 12,
                  borderLeft: `3px solid ${note.hasPriorityFlags ? "#fc8181" : note.supervisorReviewed ? "#68d391" : "#63b3ed"}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{note.individualName || "Unknown"}</div>
                      <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                        {date} · {note.serviceType || "Session"} · {note.duration || "—"}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {note.hasPriorityFlags && <AlertTriangle size={12} style={{ color: "#fc8181" }} />}
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                        background: note.supervisorReviewed ? "rgba(104,211,145,0.1)" : "rgba(236,201,75,0.1)",
                        color: note.supervisorReviewed ? "#68d391" : "#ecc94b",
                      }}>
                        {note.supervisorReviewed ? "Signed" : "Unsigned"}
                      </span>
                    </div>
                  </div>
                  {note.noteData?.presenting && (
                    <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 6, lineHeight: 1.4 }}>
                      {note.noteData.presenting.substring(0, 120)}{note.noteData.presenting.length > 120 ? "..." : ""}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {memberNotes.length > 20 && (
            <div style={{ fontSize: 10, color: "#4a5568", textAlign: "center", marginTop: 8 }}>
              Showing 20 of {memberNotes.length} notes
            </div>
          )}
        </div>
      )}

      {/* CASELOAD TAB */}
      {activeTab === "caseload" && (
        <div>
          {caseload.length === 0 ? (
            <div style={{ ...C.card, textAlign: "center", padding: "32px 0" }}>
              <Users size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
              <div style={{ fontSize: 13, color: "#4a5568" }}>No individuals assigned</div>
            </div>
          ) : (
            caseload.map((ind, i) => {
              const indNotes = memberNotes.filter(n =>
                (n.individualName || "").toLowerCase().includes((ind.lastName || "").toLowerCase())
              );
              const lastNote = indNotes[0];
              const riskColors = { high: "#fc8181", moderate: "#ecc94b", low: "#68d391" };
              return (
                <div key={ind.id} style={{ ...C.card, marginBottom: 8, padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {ind.lastName}, {ind.firstName}
                      </div>
                      <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                        {ind.program || "—"} · {indNotes.length} notes
                        {lastNote && <span> · Last: {lastNote.date}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase",
                        background: `${riskColors[ind.riskLevel] || "#68d391"}15`,
                        color: riskColors[ind.riskLevel] || "#68d391",
                      }}>{ind.riskLevel || "low"}</span>
                      {ind.primaryAgent === member.id && (
                        <span style={{ fontSize: 8, fontWeight: 700, color: "#ecc94b" }}>PRIMARY</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamTab({
  C, teamMembers, teamLoading, sharedNotes, orgId,
  agents, counselors, supervisors, handleRoleChange,
  sharedIndividuals,
}) {
  const [showInvite, setShowInvite] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // If a member is selected, show their profile
  if (selectedMember) {
    return (
      <MemberProfile
        member={selectedMember}
        notes={sharedNotes}
        individuals={sharedIndividuals || []}
        C={C}
        onBack={() => setSelectedMember(null)}
      />
    );
  }

  return (
    <div>
      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total Staff", value: teamMembers.length, color: "#63b3ed" },
          { label: "Agents", value: agents.length, color: "#4fd1c5" },
          { label: "Counselors", value: counselors.length, color: "#b794f4" },
          { label: "Supervisors", value: supervisors.length, color: "#ecc94b" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Invite */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowInvite(!showInvite)} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 12,
          background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white",
          fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
        }}>
          <UserPlus size={16} /> Invite Team Member
        </button>
      </div>

      {showInvite && (
        <div style={{ ...C.card, background: "rgba(49,130,206,0.04)", border: "1px solid rgba(49,130,206,0.15)", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>Invite New Team Member</span>
            <button onClick={() => setShowInvite(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={16} style={{ color: "#718096" }} /></button>
          </div>
          <div style={{ fontSize: 12, color: "#718096", lineHeight: 1.6 }}>To add a new agent or counselor:</div>
          <ol style={{ fontSize: 12, color: "#a0aec0", lineHeight: 2, marginTop: 8, paddingLeft: 20 }}>
            <li>Have them sign up at the login page with their email</li>
            <li>They'll automatically join your organization ({orgId})</li>
            <li>Set their role below after they appear in the team list</li>
          </ol>
        </div>
      )}

      {/* Team List */}
      {teamLoading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <RefreshCw size={24} style={{ color: "#63b3ed", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>Loading team members...</div>
        </div>
      ) : teamMembers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Users size={40} style={{ color: "#2d3748", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 16, fontWeight: 800, color: "#4a5568" }}>No Team Members Yet</div>
        </div>
      ) : (
        teamMembers.map(m => {
          const memberNotes = sharedNotes.filter(n => n.agentId === m.id);
          const signed = memberNotes.filter(n => n.supervisorReviewed).length;
          const flagged = memberNotes.filter(n => n.hasPriorityFlags).length;
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          const weekNotes = memberNotes.filter(n => {
            const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date || 0);
            return d >= weekAgo;
          });

          return (
            <div key={m.id} style={{ ...C.card, marginBottom: 10, cursor: "pointer" }}
              onClick={() => setSelectedMember(m)}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: m.role === "counselor" ? "linear-gradient(135deg, #b794f4, #63b3ed)"
                      : m.role === "supervisor" || m.role === "admin" ? "linear-gradient(135deg, #ecc94b, #f6ad55)"
                      : "linear-gradient(135deg, #63b3ed, #4fd1c5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, fontWeight: 800, color: "white", flexShrink: 0,
                  }}>
                    {(m.displayName || m.email || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{m.displayName || m.email}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase",
                        background: m.role === "supervisor" || m.role === "admin" ? "rgba(236,201,75,0.1)"
                          : m.role === "counselor" ? "rgba(183,148,244,0.1)" : "rgba(99,179,237,0.08)",
                        color: m.role === "supervisor" || m.role === "admin" ? "#ecc94b"
                          : m.role === "counselor" ? "#b794f4" : "#63b3ed",
                      }}>{m.role}</span>
                      <StatusDot status={m.liveStatus || "offline"} />
                      <span style={{ fontSize: 9, color: "#4a5568", textTransform: "capitalize" }}>
                        {(m.liveStatus || "offline").replace("-", " ")}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* Mini stats */}
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "#63b3ed" }}>{weekNotes.length} this week</div>
                    <div style={{ fontSize: 10, color: "#718096" }}>{memberNotes.length} total</div>
                  </div>
                  <ChevronRight size={16} style={{ color: "#4a5568" }} />
                </div>
              </div>

              {/* Inline stats bar */}
              <div style={{ display: "flex", gap: 12, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(99,179,237,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <FileText size={10} style={{ color: "#4fd1c5" }} />
                  <span style={{ fontSize: 10, color: "#718096" }}>{memberNotes.length} notes</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <CheckCircle size={10} style={{ color: "#68d391" }} />
                  <span style={{ fontSize: 10, color: "#718096" }}>{signed} signed</span>
                </div>
                {flagged > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <AlertTriangle size={10} style={{ color: "#fc8181" }} />
                    <span style={{ fontSize: 10, color: "#fc8181" }}>{flagged} flagged</span>
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRoleModal(m); }}
                  style={{ marginLeft: "auto", fontSize: 9, color: "#4a5568", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                >
                  Change Role
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* Role Change Modal */}
      {showRoleModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          onClick={() => setShowRoleModal(null)}>
          <div style={{ ...C.card, maxWidth: 400, width: "90%", padding: 24 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Change Role</div>
            <div style={{ fontSize: 12, color: "#718096", marginBottom: 16 }}>{showRoleModal.displayName || showRoleModal.email}</div>
            {["agent", "counselor", "supervisor", "admin"].map(role => (
              <button key={role} onClick={() => { handleRoleChange(showRoleModal.id, role); setShowRoleModal(null); }} style={{
                display: "block", width: "100%", padding: "12px 16px", borderRadius: 10, marginBottom: 8,
                background: showRoleModal.role === role ? "rgba(99,179,237,0.12)" : "rgba(15,23,42,0.5)",
                color: showRoleModal.role === role ? "#63b3ed" : "#a0aec0",
                border: `1px solid ${showRoleModal.role === role ? "rgba(99,179,237,0.25)" : "rgba(99,179,237,0.06)"}`,
                fontSize: 13, fontWeight: 700, textAlign: "left", cursor: "pointer", textTransform: "capitalize",
              }}>
                {role} {showRoleModal.role === role && "← current"}
              </button>
            ))}
            <button onClick={() => setShowRoleModal(null)} style={{
              marginTop: 8, width: "100%", padding: 10, borderRadius: 10,
              background: "transparent", color: "#718096", border: "1px solid rgba(99,179,237,0.06)",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}